/**
 * local-storage.ts
 * desc: helper utility for localStorage operations
 * note: this abstraction layer provides ability for additional logic, such as: prefixing localStorage keys with userId, etc.
 */


/**
 * getItem
 * desc: gets item from localStorage
 * note: a "key" is required to get an item; a missing key yields null
 *       (JSON.parse('null')), a falsy key or parse error yields undefined —
 *       callers rely on both
 * @param  key  Item identifier
 * @return      Parsed item value
 */
export const getItem = (key: string): unknown => {
    if (key) {
        try {
            const value = window.localStorage.getItem(key);
            return JSON.parse(value as string);
        }
        catch (err) {
            console.error(err);
        }
    }
};


/**
 * removeItem
 * desc: deletes item from localStorage
 * note: a "key" is required to remove an item
 * @param  key  Item identifier
 */
export const removeItem = (key: string): void => {
    if (key) {
        window.localStorage.removeItem(key);
    }
};


/**
 * setItem
 * desc: saves item to localStorage
 * note: a "key" and a defined "value" are required to save an item
 * @param  key    Item identifier
 * @param  value  Item value (always stored as string)
 */
export function setItem(key: string, value: unknown): void {
    if (key && value != undefined) {
        try {
            const valueStr = JSON.stringify(value);
            return window.localStorage.setItem(key, valueStr);
        }
        catch (err) {
            console.error(err);
        }
    }
}
